/**
 * doseViews — the two projections an analysed row feeds to `DoseChip`.
 *
 * `DoseChip` derives everything it renders from one supplement-shaped object:
 * the amount from `getDisplayDosage`, the frequency from
 * `dosageSource === 'pdf_verified'`, the clinical-study tooltip from
 * `isClinicalStudyDose`. An analysed row has to draw **two** chips off one
 * object — the member's amount and the study's — and STACK-544 names the way
 * that goes wrong: *"if the member's typed dose ever renders in the study chip,
 * a member reads their own number as study-backed, and a test on the chip alone
 * does not catch the swap."*
 *
 * So neither chip is handed the row. Each is handed a projection built here,
 * and the projections are built so the wrong outcome is **structurally
 * impossible** rather than merely not currently happening:
 *
 * - The member's projection carries `dosage` and `unit` and nothing else. With
 *   no `dosageSource` there is no frequency, and `isClinicalStudyDose` answers
 *   false, so the tooltip cannot render — not because a flag says not to, but
 *   because the data a tooltip would be built from is not in the object.
 * - The study's projection is read only from `studyDose`. It has **no fallback
 *   to the row's own `dosage`**: a supplement with no study dose returns null
 *   and draws no second chip, which is the ruled behaviour for a supplement in
 *   the library with no studies for the goal.
 */

import { ENTRY_STATES } from './mergeAnalysis';
import {
  ACTION_FOR_ENTRY_STATE,
  STANDARD_DOSE_LABEL,
  STUDIED_DOSE_LABEL,
  addAmountMessage,
  checkAmountMessage,
  safetyLimitNote,
} from './copy';
import { formatDoseAmount } from '../../utils/dosageDisplay';

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/**
 * The member's own amount, as a chip with no provenance of any kind.
 *
 * @returns {{dosage: *, unit: *}|null} null when there is no amount to show
 */
export function memberDoseView(supplement) {
  if (!supplement || isBlank(supplement.dosage)) return null;
  // Deliberately NOT a spread of `supplement`. Copying the row and deleting the
  // provenance fields would mean a field added to the row later arrives in this
  // chip by default; building the object from nothing means it never does.
  return { dosage: supplement.dosage, unit: supplement.unit ?? null };
}

/**
 * The study's amount, read only from `studyDose`.
 *
 * Keeps `dosageSource` and `dosageDetails`, which is what earns this chip the
 * clinical-study tooltip and the frequency through `DoseChip`'s existing
 * gating — no second copy of either rule.
 *
 * @returns {{dosage:*, unit:*, dosageSource:*, dosageDetails:*}|null}
 */
export function studyDoseView(supplement) {
  const study = supplement?.studyDose;
  if (!study || isBlank(study.dosage)) return null;
  return {
    dosage: study.dosage,
    unit: study.unit ?? null,
    dosageSource: study.dosageSource ?? 'study',
    dosageDetails: study.dosageDetails ?? null,
  };
}

/**
 * The row's action label, or null when the row shows a study comparison instead.
 *
 * `add_amount` and `check_amount` are the two states where the member's number
 * cannot be compared to a study's — a count rather than an amount, or an amount
 * that is implausible for its unit. Those rows get a control that opens the
 * existing Edit dose flow and **no study chip**: printing a study dose beside a
 * number that cannot be compared to it invites exactly the comparison the state
 * exists to withhold.
 *
 * **No Add amount where the analysis found no studies** (Tanu, 2026-09-27,
 * option A). The prompt asks for an amount so it can be compared with the
 * studies; with none for this goal there is nothing to compare, so a member who
 * followed it would re-analyse and still see no studied dose. The row shows the
 * amount as entered and the "No studies found" line says why. Change amount is
 * unaffected: it catches an impossible amount, studies or not.
 */
export function entryStateAction(supplement) {
  if (addAmountHasNothingToCompare(supplement)) return null;
  return ACTION_FOR_ENTRY_STATE[supplement?.entryState] || null;
}

/** An Add amount row the analysis found no studies for. See `entryStateAction`. */
function addAmountHasNothingToCompare(supplement) {
  return supplement?.entryState === ENTRY_STATES.ADD_AMOUNT && supplement?.study_count === 0;
}

/**
 * The one-line notice that goes with the action ("1c", Tanu, 2026-09-24), or
 * `null` for a row that has no action.
 *
 * - `check_amount` quotes the member's own amount, grouped for reading
 *   ("1,000 g is more than a daily dose could be. Check the weight, e.g. mg.").
 * - `add_amount` names the unit the studies use. The row's study dose is the
 *   only place that unit is known — the catalog carries none — so with no
 *   study dose the notice says so in general terms rather than guess a unit.
 */
export function entryStateMessage(supplement) {
  const state = supplement?.entryState;
  if (state === ENTRY_STATES.CHECK_AMOUNT) {
    const n = Number(supplement.dosage);
    const amount = Number.isFinite(n)
      ? n.toLocaleString('en-US', { maximumFractionDigits: 2 })
      : String(supplement.dosage ?? '');
    const unit = supplement.unit ? ` ${supplement.unit}` : '';
    return checkAmountMessage(`${amount}${unit}`);
  }
  if (state === ENTRY_STATES.ADD_AMOUNT) {
    if (addAmountHasNothingToCompare(supplement)) return null;
    return addAmountMessage(supplement?.studyDose?.unit || null);
  }
  return null;
}

/**
 * Whether this row shows the study chip.
 *
 * Both halves must hold: a study dose exists, and the row is not in an entry
 * state that withholds the comparison. Read off the state itself, not off
 * `entryStateAction`: whether a row is *prompted* (see the no-studies case
 * there) must never decide whether a count is drawn beside a study dose.
 */
export function showsStudyDose(supplement) {
  if (ACTION_FOR_ENTRY_STATE[supplement?.entryState]) return false;
  if (supplement?.entryState === ENTRY_STATES.NOT_IN_LIBRARY) return false;
  return studyDoseView(supplement) !== null;
}

/**
 * Whether the backend replaced this row's studied dose with the signed
 * standard because the studied dose was above the signed safety limit.
 * Read off the override record the backend writes, never inferred from the
 * numbers: the limit lives in `supplements.json`, not here.
 */
function heldToSafetyLimit(supplement) {
  const study = supplement?.studyDose;
  return study?.dosageSource === 'standard' && study?.dosageOverride?.reason === 'exceeds_max';
}

/** The label for the second half of the split chip, given its projection. */
export function studyDoseLabel(view) {
  return view?.dosageSource === 'standard' ? STANDARD_DOSE_LABEL : STUDIED_DOSE_LABEL;
}

/**
 * The line under a row whose studied dose was held to the safety limit —
 * "The studied dose (12 mg) is above the safe daily limit, so the standard
 * dose is shown." — or `null`. Only on a row that shows the dose at all.
 */
export function safetyLimitMessage(supplement) {
  if (!showsStudyDose(supplement) || !heldToSafetyLimit(supplement)) return null;
  const { previous_dosage: amount, previous_unit: unit } = supplement.studyDose.dosageOverride;
  if (isBlank(amount)) return null;
  return safetyLimitNote(`${formatDoseAmount(amount)}${unit ? ` ${unit}` : ''}`);
}
